import axios from 'axios';
import API_SERVICE from '@Service/apiService';
import {
    AUTHENTICATION,
    DOCUMENT_VIEWS,
    CLEAR_ERRORS,
    SEAL_NETWORK,
    SEAL_SYSTEMS,
    NOTIFICATION_HANDLER,
    DOCUMENT_SCORECARD,
    DOCUMENT_MARKUP
} from '@Constants/types';
import {
    STORAGE_TYPE,
    getFromStorage,
    setInStorage,
    removeFromStorage
} from '@Service/storageService';
import store from '@Store';
import { DocumentStatus } from '@Constants/status';
import GetWordVersion, { getBasename, whichBrowser } from '@Utils/commonUtils';
import { getSealVersionPromise } from './sealAPIActions';
import { getDigAppsPromise } from './scoreCardActions';
import { getSealViewsPromise } from './documentActions';
import diffInMinutes from '@Utils/dateUtils';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import { resetAllFilterFlags } from './filterActions';
import { isAfterOrEqual } from '@Utils/versionUtils';
import { errorMessages } from '@Service/versionService';
import { isAuthorized } from '@Service/authentication';
import { getPermissionByVersion } from '@Service/versionService';
import environment from '../environment/version';

/* global Office */
let dlgauth2 = null;
let dlgsaml = null;
let retry = 0;

export function loginSuccessHandler() {
    const authType = getFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);
    const systemUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
    const systemName = getFromStorage('friendly_name', STORAGE_TYPE.LOCAL_STORAGE);

    let info = `userInformation:`;
    info = `${info}browserAgent=${whichBrowser()}`;
    info = `${info},wordPlatform=${GetWordVersion().platform}`;
    info = `${info},wordVersion=${GetWordVersion().majorVersion}`;
    info = `${info},addinVersion=${
        environment.version !== '{BUILD_VERSION}' ? environment.version : 'DEV'
    }`;
    info = `${info},authType=${authType}`;
    info = `${info},systemName=${systemName}`;
    info = `${info},systemUrl=${systemUrl}`;

    addLog(LOG_TYPE.INFO, 'authentication', info);

    addLog(LOG_TYPE.INFO, 'authentication.loginSuccessHandler', '/seal-ws/security/user');

    API_SERVICE.refresh();
    API_SERVICE.SEAL_API({ method: 'GET', url: '/seal-ws/security/user' })
        .then(({ status, data }) => {
            const { active, username } = data.user;
            //handle if SSO user is locked

            if (!active) {
                removeFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
                removeFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);

                stashToken('', '', username);

                return API_SERVICE.handleError(
                    {
                        response: {
                            data: {
                                status: 401,
                                message: errorMessages.LOCKED_ACCOUNT.legacy
                            }
                        }
                    },
                    'authentication.loginSuccessHandler'
                );
            }

            if (status === 200) {
                store.dispatch({
                    type: AUTHENTICATION.SIGNED_IN,
                    payload: data.user
                });
            } else {
                const errMessage = `Unexpected HTTP Status code:${status}`;
                addLog(LOG_TYPE.ERROR, 'authentication.loginSuccessHandler', errMessage);

                store.dispatch({
                    type: NOTIFICATION_HANDLER.ERROR,
                    payload: errMessage
                });
            }
        })
        .catch(() => {
            removeFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
            removeFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);
            store.dispatch({ type: SEAL_NETWORK.UNAUTHORIZED });
        });
}

export function fetchLoginData(userWithPermissionsNotLoaded) {
    let isUserAuthorized = false;
    API_SERVICE.refresh();
    API_SERVICE.SEAL_API({
        method: 'GET',
        url: '/seal-ws/security/user/permissions'
    })
        .then(res => {
            const permissions = res.data.user.permissions.map(p => p.name);
            return getSealVersionPromise(permissions, res.status);
        })
        .then(res => {
            isUserAuthorized = isAuthorized(
                {
                    ...userWithPermissionsNotLoaded,
                    permissions: res.permissions
                },
                getPermissionByVersion('scorecard', res.sealVersion)
            );
            if (isUserAuthorized)
                return getDigAppsPromise(res.sealVersion, res, userWithPermissionsNotLoaded.id);
            return Promise.resolve(res);
        })
        .then(res => {
            if (
                res.permissions.includes('VIEWS_ALL_VIEW') ||
                res.permissions.includes('VIEWS_READ')
            )
                return getSealViewsPromise(res);
            return Promise.resolve(res);
        })
        .then(res => {
            if (res.status === 200) {
                const { permissions } = res;
                let baseUrl = 'No permissions to retrieve baseURL';
                if (res && res.res && res.res.config) {
                    const { baseURL } = res.res.config;
                    baseUrl = baseURL;
                }
                const documentId = getFromStorage('sealAtnDocumentId', STORAGE_TYPE.PROPERTY_BAG);
                const sealSystems = getFromStorage('sealSystems', STORAGE_TYPE.LOCAL_STORAGE);
                const authType = getFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);
                const { releaseVersion } = store.getState().auth;
                const addinVersion =
                    environment.version !== '{BUILD_VERSION}' ? environment.version : 'Unknown';
                store.dispatch({
                    type: AUTHENTICATION.LOGIN_SUCCESS,
                    payload: {
                        ...userWithPermissionsNotLoaded,
                        permissions
                    },
                    mixpanelData: {
                        system: baseUrl,
                        documentId,
                        browserLanguage: navigator.language,
                        numberOfConfiguredServers: JSON.parse(sealSystems).length,
                        authType: authType === 'basic' ? 'Credentials' : authType,
                        sealInsightVersion: releaseVersion,
                        addinVersion
                    }
                });
                store.dispatch({ type: CLEAR_ERRORS });

                const docStatus = getFromStorage(
                    'sealAtnDocumentStatus',
                    STORAGE_TYPE.PROPERTY_BAG
                );
                const sealUrlSubmittedOn = getFromStorage('sealUrl', STORAGE_TYPE.PROPERTY_BAG);
                const selectedSystemUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
                if (
                    documentId &&
                    docStatus === DocumentStatus.PROCESSED &&
                    sealUrlSubmittedOn &&
                    selectedSystemUrl &&
                    sealUrlSubmittedOn !== selectedSystemUrl
                ) {
                    store.dispatch({ type: DOCUMENT_SCORECARD.CLEAR_SCORECARD });
                }
                if (isAfterOrEqual('8.0.0', API_SERVICE.getVersion())) {
                    if (isUserAuthorized) checkBetaFeature('markup');
                } else {
                    store.dispatch({
                        type: AUTHENTICATION.CLEAR_BETA_FEATURES
                    });
                }
            } else {
                const errMessage = `Unexpected HTTP Status code:${res.status}`;
                addLog(LOG_TYPE.ERROR, 'authentication.loginSuccessHandler', errMessage);

                store.dispatch({
                    type: NOTIFICATION_HANDLER.ERROR,
                    payload: errMessage
                });
            }
        })
        .catch(err => API_SERVICE.handleError(err, 'authentication.loginSuccessHandler'));
}

function checkBetaFeature(betaFeatureName) {
    API_SERVICE.DIGAPP_API({
        method: 'GET',
        url: `/v1/system-configurations/beta-feature?betaFeatureName=${betaFeatureName}`
    })
        .then(response => {
            const { data: betaFeatureEnabled } = response.data;
            betaFeatureEnabled
                ? store.dispatch({
                      type: AUTHENTICATION.SET_BETA_FEATURE,
                      payload: betaFeatureName
                  })
                : store.dispatch({
                      type: AUTHENTICATION.REMOVE_BETA_FEATURE,
                      payload: betaFeatureName
                  });
        })
        .catch(() => {
            store.dispatch({ type: AUTHENTICATION.CLEAR_BETA_FEATURES });
        });
}

export const checkIfUserIsActive = () => async () => {
    API_SERVICE.refresh();
    const res = await API_SERVICE.SEAL_API({ method: 'GET', url: '/seal-ws/v5/users/me' });

    if (res.status === 200 && res.data.active === true) return res.data.active;

    return false;
};

export function isCurrentSystemExpired(id) {
    try {
        let sealSystems = [];
        const availableSystems = getFromStorage('sealSystems', STORAGE_TYPE.LOCAL_STORAGE);

        if (availableSystems) {
            sealSystems = JSON.parse(availableSystems);
        }

        if (sealSystems) {
            let selectedAuthToken = '';
            let selectedAuthType = '';
            let selectedTokenIssueTime = '';

            // find the system in the systems collection based on the system Id
            const selectedSystem = sealSystems.find(s => s.id === id);
            if (selectedSystem) {
                selectedAuthToken = selectedSystem.authToken;
                selectedAuthType = selectedSystem.authType;
                selectedTokenIssueTime = selectedSystem.tokenIssueTime;
            }

            // calculate the lifespan of the selected auth token
            let tokenLifeInMinutes = 999;
            if (selectedTokenIssueTime && selectedTokenIssueTime !== '') {
                tokenLifeInMinutes = diffInMinutes(
                    new Date(),
                    new Date(parseInt(selectedTokenIssueTime, 10))
                );
            }
            if (selectedAuthToken !== '' && selectedAuthType !== '' && tokenLifeInMinutes < 240) {
                return false;
            }

            return true;
        }

        return true;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'authentication.isCurrentSystemExpired', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });

        return true;
    }
}

export function isTokenValid() {
    try {
        const authToken = getFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
        if (authToken) loginSuccessHandler();
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'authentication.isTokenValid', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

// Store (or Clear) the AuthToken and AuthType for the currently selected system
export function stashToken(authToken, authType, principal) {
    try {
        const id = getFromStorage('selectedSystemId', STORAGE_TYPE.LOCAL_STORAGE);
        if (id) {
            // read all the seal systems from the end-users' local_storage
            let availableSystems = [];
            const sealSystems = getFromStorage('sealSystems', STORAGE_TYPE.LOCAL_STORAGE);
            if (sealSystems) {
                availableSystems = JSON.parse(sealSystems);
            }

            // find the system in the systems collection based on the system Id
            if (availableSystems) {
                const selectedSystem = availableSystems.find(s => s.id === id);
                if (selectedSystem) {
                    selectedSystem.authToken = authToken;
                    selectedSystem.authType = authType;
                    selectedSystem.principal = principal || selectedSystem.principal;

                    if (authToken !== '') {
                        selectedSystem.tokenIssueTime = Date.now();
                    } else {
                        selectedSystem.tokenIssueTime = '';
                    }

                    updateSealSystem(
                        selectedSystem.sealUrl,
                        selectedSystem.friendlyName,
                        'v3',
                        authToken,
                        authType,
                        selectedSystem.tokenIssueTime,
                        selectedSystem.principal
                    );
                }
            }

            // stash the systems collection in the end-user's local storage
            setInStorage(
                'sealSystems',
                JSON.stringify(availableSystems),
                STORAGE_TYPE.LOCAL_STORAGE
            );
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'authentication.stashToken', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

export function login(principal, password) {
    try {
        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');

        return dispatch => {
            dispatch({ type: AUTHENTICATION.LOGIN_REQUEST, payload: principal });
            return API_SERVICE.SEAL_API.get('/seal-ws/v5/security/nonce')
                .then(response => {
                    const nonce = response.data;
                    if (nonce.length === 32) {
                        setInStorage('nonce', nonce, STORAGE_TYPE.LOCAL_STORAGE);
                        API_SERVICE.SEAL_API.post('/seal-ws/v5/auths', {
                            principal,
                            password,
                            nonce
                        })
                            .then(result => {
                                const xSessionToken = result.headers['x-session-token'];
                                setInStorage(
                                    'authToken',
                                    xSessionToken,
                                    STORAGE_TYPE.LOCAL_STORAGE
                                );
                                setInStorage('authType', 'basic', STORAGE_TYPE.LOCAL_STORAGE);
                                stashToken(xSessionToken, 'basic', principal);

                                loginSuccessHandler();
                            })
                            .catch(err => API_SERVICE.handleError(err, 'authentication.login'));
                    } else {
                        const err = new Error('Please submit a valid url');
                        addLog(LOG_TYPE.ERROR, 'authentication.login', err);
                        throw err;
                    }
                })
                .catch(err => {
                    store.dispatch({
                        type: SEAL_NETWORK.ERROR_MESSAGE,
                        payload:
                            "The system can't be reached, check if the server/network is properly configured then try again."
                    });
                    addLog(LOG_TYPE.ERROR, 'authentication.login', err);
                });
        };
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'authentication.login', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return null;
    }
}

export function checkSSO(systemUrl) {
    store.dispatch({ type: SEAL_SYSTEMS.SSO_CHECK, systemUrl });
    axios({
        method: 'get',
        url: `${systemUrl}/seal-ws/security/sso`,
        responseType: 'application/json; charset=utf-8',
        dataType: 'json',
        timeout: 5000
    })
        .then(response => {
            const { type } = response.data.sso;
            if (type === 'none') {
                store.dispatch({ type: SEAL_SYSTEMS.SSO_NOT_CONFIGURED, systemUrl });
            } else {
                store.dispatch({ type: SEAL_SYSTEMS.SSO_CONFIGURED, systemUrl });
            }
        })
        .catch(() => {
            /** enable the SSO button even when this API request throws an exception
             * which will make it easier for field and support teams to figure things out
             * when they see the error message
             */
            store.dispatch({ type: SEAL_SYSTEMS.SSO_CONFIGURED, systemUrl });
        });
}

export function loginUsingSSO() {
    // Clear the existing tokens when attempting to sign in with SSO
    // This is required for SSO/Edge as we're polling for the auth token
    // to be set to indicate a successful login given that messageParent
    // does not work in the Edge browser
    removeFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
    removeFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);
    removeFromStorage('saml_nonce', STORAGE_TYPE.LOCAL_STORAGE);
    store.dispatch({ type: AUTHENTICATION.LOGOUT_SUCCESS });

    try {
        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');
        addLog(LOG_TYPE.INFO, 'authentication.loginUsingSSO', '/seal-ws/security/sso');
        return dispatch => {
            dispatch({ type: AUTHENTICATION.SSO_REQUEST });
            return API_SERVICE.SEAL_API.get('/seal-ws/security/sso')
                .then(response => {
                    addLog(LOG_TYPE.INFO, 'authentication.loginUsingSSO', response);
                    let ssoType = '';
                    let ssoUrl = '';
                    if (response && response.data && response.data.sso && response.data.sso.type) {
                        ssoType = response.data.sso.type;
                    }
                    if (response && response.data && response.data.sso && response.data.sso.url) {
                        ssoUrl = response.data.sso.url;
                    }
                    if (ssoType === 'saml') {
                        // get provider urls
                        API_SERVICE.SEAL_API.get('/seal-ws/security/saml/providers')
                            .then(result => {
                                const samlProviderUrl = result.data.saml.providers[0];
                                loginUsingSAML(ssoUrl, samlProviderUrl);
                            })
                            .catch(err =>
                                API_SERVICE.handleError(err, 'authentication.loginUsingSSO')
                            );
                    } else if (ssoType === 'oauth2') {
                        // oAuth2
                        loginUsingOAuth();
                    } else {
                        const errMsg = 'SSO account is not configured.';
                        dispatch({
                            type: AUTHENTICATION.SSO_FAILURE,
                            message: errMsg
                        });
                    }
                })
                .catch(err => API_SERVICE.handleError(err, 'authentication.loginUsingSSO'));
        };
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'authentication.loginUsingSSO', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return null;
    }
}

export function loginUsingSAML(samlUrl, samlProviderUrl) {
    try {
        store.dispatch({ type: AUTHENTICATION.SSO_REQUEST });
        const sealUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
        const redirectUrl = `${window.location.protocol}//${
            window.location.host
        }${getBasename()}/login`;

        // Setup the pop-up dialog options, if this is the installed version of MSWord on a Mac then we need to set different options
        let displayInIframe = false;
        let promptBeforeOpen = false;
        const versionInfo = GetWordVersion();
        if (!versionInfo.isWordOnline && versionInfo.platform === 'Mac') {
            displayInIframe = true;
            promptBeforeOpen = true;
        }

        document.cookie = 'saml_nonce=none';
        setTimeout(() => checkSAMLStatus(), 3000);

        if (typeof sealUrl !== 'undefined' && sealUrl) {
            const oAuthUrl = `${sealUrl}/seal-ws${samlUrl}?redirect_uri=${redirectUrl}&idp=${samlProviderUrl}`;
            addLog(LOG_TYPE.INFO, 'authentication.loginUsingSAML', oAuthUrl);
            Office.context.ui.displayDialogAsync(
                oAuthUrl,
                {
                    height: 80,
                    width: 80,
                    displayInIframe,
                    promptBeforeOpen
                },
                asyncResult => {
                    addLog(LOG_TYPE.INFO, 'authentication.loginUsingSAML', asyncResult);
                    if (asyncResult.status !== 'failed') {
                        dlgsaml = asyncResult.value;
                        retry = 0;

                        dlgsaml.addEventHandler(Office.EventType.DialogMessageReceived, args => {
                            if (args.type === 'dialogMessageReceived') {
                                dlgsaml.close();
                            } else {
                                store.dispatch({
                                    type: AUTHENTICATION.SSO_FAILURE,
                                    message:
                                        'We seem to have a problem signing you in using SSO, until the issue has been resolved try using credentials instead.'
                                });
                            }
                        });
                    } else {
                        if (asyncResult.error && asyncResult.error.message) {
                            addLog(
                                LOG_TYPE.ERROR,
                                'authentication.loginUsingSAML',
                                asyncResult.error
                            );
                            store.dispatch({
                                type: SEAL_NETWORK.ERROR_MESSAGE,
                                payload: asyncResult.error.message
                            });

                            return;
                        }

                        // no error information, so just display the generic server down message
                        store.dispatch({ type: SEAL_NETWORK.SERVER_DOWN_ERROR });
                    }
                }
            );
        } else {
            store.dispatch({ type: SEAL_NETWORK.ERROR });
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'authentication.loginUsingSAML', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

export function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts
            .pop()
            .split(';')
            .shift();
    }
}

export function checkAuth2Status() {
    let authTokenCookie = getCookie('authToken');
    if (authTokenCookie !== undefined && authTokenCookie !== null) {
        authTokenCookie = authTokenCookie.trim();
    }

    let authTypeCookie = getCookie('authType');
    if (authTypeCookie !== undefined && authTypeCookie !== null) {
        authTypeCookie = authTypeCookie.trim();
    }

    const authToken = getFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
    if (
        (authToken !== undefined && authToken && authToken.length > 0) ||
        (authTokenCookie !== undefined && authTokenCookie !== 'none')
    ) {
        if (dlgauth2) {
            try {
                dlgauth2.close();
            } catch {
                // do nothing
            }
            dlgauth2 = null;
        }

        // belts and braces update, primarily for oldEdge as we are relying on the cookie
        if (authTypeCookie === 'OAuth2') {
            setInStorage('authType', authTypeCookie, STORAGE_TYPE.LOCAL_STORAGE);
            setInStorage('authToken', authTokenCookie, STORAGE_TYPE.LOCAL_STORAGE);
            stashToken(authTokenCookie, 'OAuth2');
        }

        loginSuccessHandler();
        store.dispatch({ type: AUTHENTICATION.SSO_SUCCESS });
    } else {
        retry += 1;
        if (retry > 30) {
            // dont loop forever.  If we have acquired an access token after 60 seconds then give up and close the pop-up dialog
            try {
                dlgauth2.close();
            } catch {
                // do nothing
            }
            dlgauth2 = null;
            retry = 0;

            store.dispatch({
                type: NOTIFICATION_HANDLER.WARNING,
                payload:
                    'Sign-in timed out!  You must complete the sign-in within 60 seconds.  Please retry.'
            });
            return;
        }

        setTimeout(() => checkAuth2Status(), 3000);
    }
}

export function checkSAMLStatus() {
    let samlNonceCookie = getCookie('saml_nonce');
    if (samlNonceCookie !== undefined && samlNonceCookie !== null) {
        samlNonceCookie = samlNonceCookie.trim();
    }
    let samlNonce = getFromStorage('saml_nonce', STORAGE_TYPE.LOCAL_STORAGE);
    if (
        (samlNonce !== undefined && samlNonce && samlNonce.length > 0) ||
        (samlNonceCookie !== undefined && samlNonceCookie !== 'none')
    ) {
        if (dlgsaml) {
            try {
                dlgsaml.close();
            } catch {
                // do nothing
            }
            dlgsaml = null;
        }

        removeFromStorage('saml_nonce', STORAGE_TYPE.LOCAL_STORAGE);

        // belts and braces, primarily for oldEdge as we are relying on the cookie
        if (samlNonceCookie !== undefined && samlNonceCookie !== 'none') {
            samlNonce = samlNonceCookie;
        }

        // do the remainder of the saml/nonce exchange
        const tokenUrl = `/seal-ws/security/token?nonce=${samlNonce}`;

        addLog(LOG_TYPE.INFO, 'authentication.checkSAMLStatus', tokenUrl);

        API_SERVICE.SEAL_API.get(tokenUrl)
            .then(result => {
                addLog(LOG_TYPE.INFO, 'authentication.checkSAMLStatus', result);
                if (result.status === 200) {
                    const xSessionToken = result.headers['x-session-token'];
                    setInStorage('authToken', xSessionToken, STORAGE_TYPE.LOCAL_STORAGE);
                    setInStorage('authType', 'SAML', STORAGE_TYPE.LOCAL_STORAGE);
                    stashToken(xSessionToken, 'SAML');
                    loginSuccessHandler();
                    store.dispatch({
                        type: AUTHENTICATION.SSO_SUCCESS
                    });
                } else {
                    const errMessage = `Unexpected HTTP Status code:${result.status}`;
                    addLog(LOG_TYPE.ERROR, 'authentication.checkSAMLStatus', errMessage);

                    store.dispatch({
                        type: AUTHENTICATION.SSO_FAILURE,
                        message:
                            'We seem to have a problem signing you in using SSO, until the issue has been resolved try using credentials instead.'
                    });
                }
            })
            .catch(err => API_SERVICE.handleError(err, 'authentication.checkSAMLStatus'));
    } else {
        retry += 1;
        if (retry > 30) {
            // don't loop forever.  If we have acquired an access token after 60 seconds then give up and close the pop-up dialog
            try {
                dlgsaml.close();
            } catch {
                // do nothing
            }
            dlgsaml = null;
            retry = 0;
            removeFromStorage('saml_nonce', STORAGE_TYPE.LOCAL_STORAGE);

            store.dispatch({
                type: NOTIFICATION_HANDLER.WARNING,
                payload:
                    'Sign-in timed out!  You must complete the sign-in within 60 seconds.  Please retry.'
            });

            return;
        }

        setTimeout(() => checkSAMLStatus(), 3000);
    }
}

export function loginUsingOAuth() {
    try {
        store.dispatch({ type: AUTHENTICATION.SSO_REQUEST });
        const sealUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
        const redirectUrl = `${window.location.protocol}//${
            window.location.host
        }${getBasename()}/login`;

        // Setup the pop-up dialog options, if this is the installed version of MSWord on a Mac then we need to set different options
        let displayInIframe = false;
        let promptBeforeOpen = false;
        const versionInfo = GetWordVersion();
        if (!versionInfo.isWordOnline && versionInfo.platform === 'Mac') {
            displayInIframe = true;
            promptBeforeOpen = true;
        }

        document.cookie = 'authToken=none';
        document.cookie = 'authType=none';
        setTimeout(() => checkAuth2Status(), 3000);

        if (typeof sealUrl !== 'undefined' && sealUrl) {
            const oAuthUrl = `${sealUrl}/seal-ws/oauth2/login?redirect_uri=${redirectUrl}`;
            addLog(LOG_TYPE.INFO, 'authentication.loginUsingOAuth', oAuthUrl);
            Office.context.ui.displayDialogAsync(
                oAuthUrl,
                {
                    height: 80,
                    width: 80,
                    displayInIframe,
                    promptBeforeOpen
                },
                asyncResult => {
                    addLog(LOG_TYPE.INFO, 'authentication.loginUsingOAuth', asyncResult);
                    if (asyncResult.status !== 'failed') {
                        dlgauth2 = asyncResult.value;
                        retry = 0;

                        dlgauth2.addEventHandler(Office.EventType.DialogMessageReceived, args => {
                            if (args.type === 'dialogMessageReceived') {
                                dlgauth2.close();
                            } else {
                                store.dispatch({
                                    type: AUTHENTICATION.SSO_FAILURE,
                                    message:
                                        'We seem to have a problem signing you in using SSO, until the issue has been resolved try using credentials instead.'
                                });
                            }
                        });
                    } else {
                        if (asyncResult.error && asyncResult.error.message) {
                            store.dispatch({
                                type: SEAL_NETWORK.ERROR_MESSAGE,
                                payload: asyncResult.error.message
                            });

                            return;
                        }

                        // no error information, so just display the generic server down message
                        store.dispatch({ type: SEAL_NETWORK.SERVER_DOWN_ERROR });
                    }
                }
            );
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'authentication.loginUsingOAuth', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

export function logout(removeSelectedSystem, principal) {
    return dispatch => {
        try {
            removeFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
            removeFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);
            resetAllFilterFlags();
            if (removeSelectedSystem) stashToken('', '', principal);

            dispatch({ type: DOCUMENT_VIEWS.CLEAR_VIEWS });
            dispatch({ type: AUTHENTICATION.LOGOUT_SUCCESS });
            dispatch({ type: DOCUMENT_MARKUP.CLEAR_BACKGROUND_PROCESS_DATA });
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'authentication.logout', err);
            dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };
}

export function updateSealSystem(
    sealUrl,
    sealName,
    sealVersion,
    authToken,
    authType,
    expiryTokenTime,
    authSection,
    principal
) {
    const newSystem = {
        sealUrl,
        sealName,
        sealVersion,
        authToken,
        authType,
        expiryTokenTime,
        authSection,
        principal
    };
    return store.dispatch({ payload: newSystem, type: SEAL_SYSTEMS.SEAL_SYSTEM_UPDATE });
}

export function removeSealSystem(sealUrl) {
    return store.dispatch({ payload: sealUrl, type: SEAL_SYSTEMS.SEAL_SYSTEM_REMOVE });
}
// Most probably there will be no Need for delete all and save all as long as
// the app is synced and in the scope of the current user cases

// export function removeAllSealSystems() {
//     return store.dispatch({ type: SEAL_SYSTEMS.SEAL_SYSTEMS_REMOVE_ALL });
// }

export function updateAllSealSystems(systems) {
    return store.dispatch({ payload: systems, type: SEAL_SYSTEMS.SEAL_SYSTEMS_UPDATE_ALL });
}
